/**
 * "What to do" screen shown before a camera step opens (ID card, selfie).
 *
 * Mounted by FlowRunner IN FRONT of the capture widget rather than inside it:
 * the widgets request the camera in a mount effect, so the browser's permission
 * prompt now follows a deliberate tap on a screen that says why, instead of
 * landing on the applicant unexplained. Purely local — no engine event fires.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { DocumentKind } from './documents';
import { Illustration } from '../ui/Illustration';

export type GuideKind = 'document' | 'selfie';

interface Tip {
  key: string;
  icon: ReactNode;
  fallback: string;
}

const TIPS: Record<GuideKind, Tip[]> = {
  document: [
    { key: 'original', icon: <IconCard />, fallback: 'Use the original card, not a photocopy or a screen' },
    { key: 'surface', icon: <IconSurface />, fallback: 'Lay it flat on a dark surface' },
    { key: 'glare', icon: <IconSun />, fallback: 'Keep all four corners in view, with no glare' },
  ],
  selfie: [
    { key: 'light', icon: <IconSun />, fallback: 'Find bright, even light' },
    { key: 'clear', icon: <IconFace />, fallback: 'Remove sunglasses, hats and masks' },
    { key: 'prompts', icon: <IconPrompt />, fallback: 'Follow the prompts on screen' },
  ],
};

const COPY: Record<GuideKind, { title: string; lead: string }> = {
  document: {
    title: 'Photograph your ID card',
    lead: 'You’ll take a photo of the front, then the back.',
  },
  selfie: {
    title: 'Take a selfie',
    lead: 'We’ll compare it with the photo on your ID.',
  },
};

/** Copy namespace per document. `document` is the Thai ID card's; the others
 *  override only what differs (a licence is still a card; a passport is not). */
const DOCUMENT_COPY: Record<DocumentKind, string> = {
  thai_id: 'document',
  driving_license: 'drivingLicense',
  passport: 'passport',
};

export function CaptureGuide({
  kind,
  document = 'thai_id',
  onStart,
  onChangeDocument,
}: {
  kind: GuideKind;
  /** Which document the photo is of (document guides only). */
  document?: DocumentKind;
  onStart: () => void;
  /** Back to the document choice, when the applicant made one. */
  onChangeDocument?: () => void;
}) {
  const { t } = useTranslation();
  const ns = kind === 'selfie' ? 'selfie' : DOCUMENT_COPY[document];
  // A document override falls back to the Thai card's copy, then to English.
  const copy = (key: string, fallback: string) =>
    t(`flow.guide.${ns}.${key}`, { defaultValue: t(`flow.guide.${kind}.${key}`, { defaultValue: fallback }) });
  return (
    <section className="guide" data-testid={`guide-${kind}`}>
      <Illustration kind={kind === 'document' ? 'id-card' : 'selfie'} />
      <h1 className="guide-title">{copy('title', COPY[kind].title)}</h1>
      <p className="guide-lead">{copy('lead', COPY[kind].lead)}</p>
      <ul className="guide-tips">
        {TIPS[kind].map((tip) => (
          <li key={tip.key}>
            <span className="guide-tip-icon" aria-hidden="true">
              {tip.icon}
            </span>
            <span>{copy(`tips.${tip.key}`, tip.fallback)}</span>
          </li>
        ))}
      </ul>
      <button type="button" className="btn guide-start" onClick={onStart} data-testid="journey-start" data-track="open_camera">
        {t('flow.guide.start', { defaultValue: 'Open camera' })}
      </button>
      {onChangeDocument ? (
        <button
          type="button"
          className="btn btn--ghost"
          onClick={onChangeDocument}
          data-track="change_document"
          data-testid="change-document"
        >
          {t('flow.guide.changeDocument')}
        </button>
      ) : null}
    </section>
  );
}

function Svg({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function IconCard() {
  return (
    <Svg>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <circle cx="8" cy="11" r="2" />
      <path d="M5 16c.6-1.6 1.7-2.4 3-2.4s2.4.8 3 2.4M14 10h5M14 14h3" />
    </Svg>
  );
}

function IconSurface() {
  return (
    <Svg>
      <path d="M3 17h18M6 17l2-8h8l2 8" />
    </Svg>
  );
}

function IconSun() {
  return (
    <Svg>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </Svg>
  );
}

function IconFace() {
  return (
    <Svg>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 10h.01M15 10h.01M9 15c1.7 1.3 4.3 1.3 6 0" />
    </Svg>
  );
}

function IconPrompt() {
  return (
    <Svg>
      <path d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />
      <path d="M8 9h8M8 13h5" />
    </Svg>
  );
}
